import type { BreadcrumbItem } from '@/components/layout/Breadcrumbs';
import type { ErpModule } from '@/api/platform.api';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import { localizeModuleLabel } from '@/i18n/navLabel';
import { resolveRoutePageTitle } from '@/config/routePageTitles';
import { buildSettingsBreadcrumbs, isSettingsPath } from '@/utils/settingsBreadcrumbs';

function normalizePath(pathname: string): string {
  const base = pathname.split('?')[0].replace(/\/+$/, '');
  return base || '/';
}

/** Short module label for breadcrumbs (e.g. HR, Finance, CRM). */
export function resolveModuleBreadcrumbLabel(pathname: string): string | undefined {
  const module = resolveModuleFromPath(pathname);
  if (!module) return undefined;
  return localizeModuleLabel(module);
}

/**
 * Build module-scoped breadcrumbs from the current route.
 * Pattern: Module > Page (no legacy Org root).
 */
export function buildBreadcrumbsFromPath(
  pathname: string,
  tail?: BreadcrumbItem[],
  options?: { activeModule?: ErpModule | null },
): BreadcrumbItem[] {
  if (tail?.length) {
    return tail;
  }

  const path = normalizePath(pathname);
  if (isSettingsPath(path)) {
    return buildSettingsBreadcrumbs(path, { activeModule: options?.activeModule });
  }
  const module = resolveModuleFromPath(path);
  if (!module) {
    const pageTitle = resolveRoutePageTitle(path);
    return pageTitle ? [{ label: pageTitle }] : [];
  }

  const moduleEntry = getModuleEntryByErpModule(module);
  const moduleHome = moduleEntry?.homePath;
  const moduleLabel = resolveModuleBreadcrumbLabel(path) ?? moduleEntry?.label ?? module;

  const pageTitle = resolveRoutePageTitle(path);
  const isModuleHome = moduleHome != null && path === normalizePath(moduleHome);

  if (isModuleHome) {
    return [{ label: moduleLabel, to: moduleHome }];
  }

  const crumbs: BreadcrumbItem[] = [];
  if (moduleHome) {
    crumbs.push({ label: moduleLabel, to: moduleHome });
  } else {
    crumbs.push({ label: moduleLabel });
  }

  if (pageTitle && pageTitle !== moduleLabel) {
    crumbs.push({ label: pageTitle });
  }

  return crumbs;
}
